import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";

export default function EmailReportDialog({ open, onClose, chantier, client }) {
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [variant, setVariant] = useState("client");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setRecipient(client?.email || "");
      setVariant("client");
      const label = "Rapport client";
      setSubject(`${label} — ${chantier?.name || ""}`);
      setMessage(
        `Bonjour${client?.name ? " " + client.name : ""},\n\n` +
        `Veuillez trouver ci-joint le rapport client pour "${chantier?.name || ""}".\n\n` +
        `Cordialement,\nBKPRO Électricité`
      );
    }
  }, [open, chantier, client]);

  useEffect(() => {
    // Update subject line when user switches variant (only if not manually edited)
    if (!open) return;
    const label = variant === "client" ? "Rapport client" : "Rapport terrain";
    setSubject(`${label} — ${chantier?.name || ""}`);
  }, [variant, chantier, open]);

  const send = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient)) return toast.error("Email invalide");
    setSending(true);
    try {
      await api.post(`/chantiers/${chantier.id}/email-report`, {
        chantier_id: chantier.id,
        recipient_email: recipient,
        subject,
        message,
        variant,
      });
      toast.success(`${variant === "client" ? "Rapport client" : "Rapport terrain"} envoyé à ${recipient}`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl" data-testid="email-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <EnvelopeSimple size={20} className="text-[#005CFF]" weight="duotone" /> Envoyer le bon
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest mb-1.5 block">Type de rapport</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setVariant("client")}
                className={`rounded-xl p-3 text-left border transition-colors ${variant === "client" ? "bg-[#005CFF]/15 border-[#005CFF]" : "bg-white/5 border-white/10 hover:border-white/20"}`}
                data-testid="variant-client"
              >
                <div className="text-xs font-display font-semibold">Rapport client</div>
                <div className="text-[10px] text-white/50 mt-0.5">Épuré — travaux, photos sélectionnées</div>
              </button>
              <button
                type="button"
                onClick={() => setVariant("field")}
                className={`rounded-xl p-3 text-left border transition-colors ${variant === "field" ? "bg-amber-500/15 border-amber-500" : "bg-white/5 border-white/10 hover:border-white/20"}`}
                data-testid="variant-field"
              >
                <div className="text-xs font-display font-semibold">Rapport terrain</div>
                <div className="text-[10px] text-white/50 mt-0.5">Complet — matériel, heures, obs.</div>
              </button>
            </div>
          </div>
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Destinataire</Label>
            <Input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="client@example.com"
              className="bg-white/5 border-white/10 mt-1"
              data-testid="email-recipient"
            />
          </div>
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Objet</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="bg-white/5 border-white/10 mt-1" data-testid="email-subject" />
          </div>
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Message</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} className="bg-white/5 border-white/10 mt-1" data-testid="email-message" />
          </div>
          <div className="rounded-lg bg-white/5 border border-white/10 p-2 flex items-center gap-2 text-[11px] text-white/60">
            <span className="w-6 h-6 rounded bg-red-500/20 flex items-center justify-center text-red-400 text-[10px] font-bold">PDF</span>
            <span>{variant === "client" ? "Rapport client (version épurée)" : "Rapport terrain (version complète)"} en pièce jointe</span>
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="ghost" onClick={onClose} className="flex-1 border border-white/10">Annuler</Button>
            <Button onClick={send} disabled={sending} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="email-send-btn">
              <PaperPlaneTilt size={14} weight="bold" /> {sending ? "Envoi…" : "Envoyer"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
